import * as React from 'react'

import { Box, Chevron, Text } from '@factorialco/deprecated-design-system'
import { NewStatsCounterAllowanceStatsDocument } from 'generated/resources/graphql'
import moment from 'moment'

import Loading from 'modules/core/components/Loading'
import { useLazyQuery } from 'modules/core/hooks/useQuery'
import { i18nDateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import Balance from 'modules/timeoff/components/Leaves/Overview/NewStatsCounter/Balance'
import CycleSelector from 'modules/timeoff/components/Leaves/Overview/NewStatsCounter/CycleSelector'
import TakenDays from 'modules/timeoff/components/Leaves/Overview/NewStatsCounter/Taken'
import TotalDays from 'modules/timeoff/components/Leaves/Overview/NewStatsCounter/Total'
import type Allowance from 'modules/timeoff/store/models/Allowance'

import type {
  Cycle,
  ParsedAllowanceStat,
} from 'modules/timeoff/components/Leaves/Overview/NewStatsCounter/allowanceStatUtils'
import { transformAllowanceStat } from 'modules/timeoff/components/Leaves/Overview/NewStatsCounter/allowanceStatUtils'

interface Props {
  employeeId: string
  allowance: Allowance
  currentStat: ParsedAllowanceStat
  currentCycle: Cycle
}

const Counter = ({ employeeId, allowance, currentStat, currentCycle }: Props) => {
  const today = React.useMemo(() => moment(), [])
  const [loading, setLoading] = React.useState(false)
  const [open, setOpen] = React.useState(false)
  const [selectedStat, setStat] = React.useState<ParsedAllowanceStat>(currentStat)
  const [selectedCycle, setCycle] = React.useState<Cycle>(currentCycle)
  const nextCycle = selectedStat.nextCycle(selectedCycle)

  const [fetchAllowanceStats] = useLazyQuery(NewStatsCounterAllowanceStatsDocument, {
    fetchPolicy: 'network-only',
  })

  const onSelect = async (cycleId: string) => {
    setLoading(true)

    const cycle = selectedStat.cycles.find((c) => c.id === cycleId)

    if (!cycle || cycleId === selectedCycle.id) {
      setLoading(false)
      return
    }

    if (cycleId === currentCycle.id) {
      setStat(currentStat)
      setCycle(currentCycle)
      setLoading(false)
      return
    }

    const referenceDate = cycle.end.isBefore(today) ? cycle.end : cycle.begin
    const referenceDateString = i18nDateToString(referenceDate, 'backend')

    try {
      const { data, error } = await fetchAllowanceStats({
        variables: {
          employeeId,
          allowanceIds: [allowance.attributes.id],
          referenceDate: referenceDateString,
        },
      })

      if (error) {
        // On error, keep current selection - query failed so we maintain the previous state
        setLoading(false)
        return
      }

      const allowanceStatNode = data?.employees?.employee?.allowanceStatsConnection?.nodes?.[0]

      if (allowanceStatNode) {
        const transformedStat = transformAllowanceStat(allowanceStatNode)
        setStat(transformedStat)
        setCycle(cycle)
      }
    } catch {
      // Network or unexpected errors - keep current selection
    }

    setLoading(false)
  }

  if (loading) return <Loading />

  return (
    <>
      {open && (
        <Box background="grey100" width="full">
          <CycleSelector
            cycles={selectedStat.cycles}
            currentCycle={currentCycle}
            selectedCycle={selectedCycle}
            onSelect={onSelect}
          />
          <TotalDays
            allowance={allowance}
            employeeId={employeeId}
            selectedCycle={selectedCycle}
            selectedStat={selectedStat}
          />
          <TakenDays
            allowance={allowance}
            selectedStat={selectedStat}
            selectedCycle={selectedCycle}
            nextCycle={nextCycle}
          />
          <Balance
            allowance={allowance}
            selectedStat={selectedStat}
            selectedCycle={selectedCycle}
            nextCycle={nextCycle}
          />
        </Box>
      )}
      <Box
        flexDirection="row"
        gap="s8"
        alignItems="center"
        onClick={() => setOpen(!open)}
        cursor="pointer"
      >
        <Text.Normal color="viridian1200">
          {!open
            ? i18n.t('time_off.new_counter.view_more')
            : i18n.t('time_off.new_counter.view_less')}
        </Text.Normal>
        <Chevron type="brand" direction={open ? 'up' : 'down'} />
      </Box>
    </>
  )
}

export default Counter
